// Живой список бэкендов сигнального WebSocket.
//
// Источник списка (по убыванию приоритета):
//   1. ?url=<wss> в адресной строке — отладочный override, возвращает ровно [url].
//   2. window.__BACKENDS__ — глобал, который кладёт <script src="/backends.js">
//      с apex-домена (загружается до бандла). Форма: {base, backends}, где
//      base — базовый сегмент (строка), backends — имена нод (массив строк).
//      Домен НЕ передаётся: URL собираются по apex СВОЕГО домена.
//   3. Фолбэк, вкомпиленный в бандл (страница обязана коннектиться и без /backends.js):
//      имена нод VITE_BACKEND_NODES × базовый сегмент VITE_BACKEND_BASE — та же форма,
//      что и глобал; либо легаси-список полных URL VITE_BACKEND_SERVERS (verbatim).
//
// Глобал и фолбэк одинаковой формы (base + имена нод) → один композитор composeUrls.

export type BackendsSource = 'url-param' | 'backends.js' | 'fallback'

// Имя ноды = один DNS-лейбл (буквы/цифры/дефис); отсекает битые записи из глобала.
const NODE_LABEL = /^[a-z0-9-]+$/i

// Результат резолва: список URL плюс диагностика источника для лога.
export interface ResolveResult {
    urls: string[]
    source: BackendsSource
    apex: string
    // Заполнен, если глобал присутствовал, но был отброшен как невалидный.
    warning?: string
}

// Всё, что резолвер читает из окружения. Вынесено в параметр ради чистоты и тестируемости.
export interface ResolveInput {
    hostname: string
    urlOverride?: string | null
    global?: unknown
    fallbackNodes?: string
    fallbackBase?: string
    fallbackServers?: string[]
}

// apex = последние два лейбла хоста: woice.ru → woice.ru, m1.woice.ru → woice.ru,
// localhost → localhost (меньше двух лейблов — хост как есть).
export function apexDomain(hostname: string): string {
    const labels = hostname.split('.')
    if (labels.length <= 2) {
        return hostname
    }
    return labels.slice(-2).join('.')
}

// Единый композитор для глобала и фолбэка: имя ноды + базовый сегмент + apex.
// Пустой base → wss://<node>.<apex>.
export function composeUrls(nodes: string[], base: string, apex: string): string[] {
    const segment = base ? `${base}.` : ''
    return nodes.map((node) => `wss://${node}.${segment}${apex}`)
}

type GlobalResult =
    | { kind: 'ok'; base: string; nodes: string[] }
    | { kind: 'absent' }
    | { kind: 'invalid'; reason: string }

function parseGlobal(global: unknown): GlobalResult {
    if (global === undefined || global === null) {
        return {kind: 'absent'}
    }
    if (typeof global !== 'object') {
        return {kind: 'invalid', reason: 'global is not an object'}
    }
    const g = global as {base?: unknown; backends?: unknown}
    if (typeof g.base !== 'string' || !g.base) {
        return {kind: 'invalid', reason: 'missing base'}
    }
    if (!Array.isArray(g.backends) || g.backends.length === 0) {
        return {kind: 'invalid', reason: 'empty backends'}
    }
    const nodes: string[] = []
    for (const node of g.backends) {
        if (typeof node !== 'string' || !NODE_LABEL.test(node)) {
            // Битая запись (пробел, точка, слэш) → не собираем кривой URL, уходим в фолбэк.
            return {kind: 'invalid', reason: 'backend node is not a valid DNS label'}
        }
        nodes.push(node)
    }
    return {kind: 'ok', base: g.base, nodes}
}

function csvNodes(raw: string | undefined): string[] {
    return (raw ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
}

function resolveFallback(input: ResolveInput, apex: string): string[] {
    const nodes = csvNodes(input.fallbackNodes)
    if (nodes.length > 0) {
        return composeUrls(nodes, input.fallbackBase ?? '', apex)
    }
    // Легаси-список полных URL: строка с '://' идёт verbatim.
    const servers = input.fallbackServers ?? []
    return servers.map((s) => (s.includes('://') ? s : composeUrls([s], input.fallbackBase ?? '', apex)[0]))
}

// Чистый резолвер: ничего не читает из окружения и не логирует.
export function resolveBackends(input: ResolveInput): ResolveResult {
    const apex = apexDomain(input.hostname)

    if (input.urlOverride) {
        return {urls: [input.urlOverride], source: 'url-param', apex}
    }

    const parsed = parseGlobal(input.global)
    if (parsed.kind === 'ok') {
        return {urls: composeUrls(parsed.nodes, parsed.base, apex), source: 'backends.js', apex}
    }

    const fallback: ResolveResult = {urls: resolveFallback(input, apex), source: 'fallback', apex}
    if (parsed.kind === 'invalid') {
        fallback.warning = `rejected /backends.js: ${parsed.reason}`
    }
    return fallback
}

function readEnv(): ResolveInput {
    const params = new URLSearchParams(window.location.search)
    return {
        hostname: window.location.hostname,
        urlOverride: params.get('url'),
        global: (window as unknown as {__BACKENDS__?: unknown}).__BACKENDS__,
        fallbackNodes: process.env.BACKEND_NODES as unknown as string | undefined,
        fallbackBase: process.env.BACKEND_BASE as unknown as string | undefined,
        fallbackServers: process.env.BACKEND_SERVERS as unknown as string[] | undefined,
    }
}

// Список URL для SDK. Читает окружение, но НЕ логирует (лог — один раз на boot).
export function resolveBackendUrls(): string[] {
    return resolveBackends(readEnv()).urls
}

// Один диагностический лог при загрузке: источник, apex-домен, итоговый список URL.
export function logBackends(): void {
    const r = resolveBackends(readEnv())
    if (r.warning) {
        console.warn(`[backends] ${r.warning}`)
    }
    console.log(`[backends] source=${r.source} apex=${r.apex} urls=${JSON.stringify(r.urls)}`)
}
